import { useState, createContext, useContext } from 'react';
import { Outlet, useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box, Stack, Typography, Avatar, IconButton, Tooltip,
  alpha, useMediaQuery, Divider, Menu, MenuItem,
  Drawer, Chip,
} from '@mui/material';
import {
  LayoutDashboard, Building2, Users, FileText, CalendarDays,
  LogOut, Sun, Moon, Menu as MenuIcon, X, ChevronRight,
} from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import { PATH_ATLAS, PATH_ADMIN } from '../../routes/paths';

// ── Theme tokens ───────────────────────────────────────────────────────────────
export const DARK = {
  bg: '#0B0E14',
  surface: '#13171F',
  surfaceHover: '#1A1F2A',
  border: 'rgba(255,255,255,0.07)',
  borderHover: 'rgba(255,255,255,0.14)',
  gold: '#D4A853',
  goldLight: '#E8C27A',
  text: '#F0F2F5',
  muted: '#6B7280',
  subtle: '#374151',
  accent: '#6366F1',
  success: '#10B981',
  warning: '#F59E0B',
  danger: '#EF4444',
  sky: '#38BDF8',
};

export const LIGHT = {
  bg: '#F4F5F7',
  surface: '#FFFFFF',
  surfaceHover: '#EEF0F3',
  border: 'rgba(0,0,0,0.08)',
  borderHover: 'rgba(0,0,0,0.15)',
  gold: '#B8882E',
  goldLight: '#D4A853',
  text: '#111827',
  muted: '#6B7280',
  subtle: '#9CA3AF',
  accent: '#6366F1',
  success: '#059669',
  warning: '#D97706',
  danger: '#DC2626',
  sky: '#0284C7',
};

// Backward compat — login page always dark
export const A = DARK;

// ── Theme context ──────────────────────────────────────────────────────────────
const AtlasThemeCtx = createContext({ theme: LIGHT, isDark: false, toggleTheme: () => {} });
export const useAtlasTheme = () => useContext(AtlasThemeCtx);

// ── Nav definition ─────────────────────────────────────────────────────────────
// Bottom tab bar — quick-access primary pages (no Calendar, keeps it uncluttered)
const BOTTOM_NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard',  path: PATH_ATLAS.dashboard },
  { icon: Building2,       label: 'Inventory',  path: PATH_ATLAS.inventory },
  { icon: Users,           label: 'Leads',      path: PATH_ATLAS.leads },
  { icon: FileText,        label: 'Proposals',  path: PATH_ATLAS.proposals },
];

// Icon rail + drawer — full page list including Calendar
const DRAWER_NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard',  path: PATH_ATLAS.dashboard },
  { icon: Building2,       label: 'Inventory',  path: PATH_ATLAS.inventory },
  { icon: Users,           label: 'Leads',      path: PATH_ATLAS.leads },
  { icon: FileText,        label: 'Proposals',  path: PATH_ATLAS.proposals },
  { icon: CalendarDays,    label: 'Calendar',   path: PATH_ATLAS.calendar },
];

const RAIL_W  = 64;  // always-collapsed icon rail (landscape)
const BOTTOMH = 60;  // bottom nav height

// ── Icon Rail (landscape only) ─────────────────────────────────────────────────
function IconRail({ currentPath, onOpenDrawer, user, isDark, toggleTheme }) {
  const { theme } = useAtlasTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const navigate = useNavigate();

  return (
    <Box sx={{
      position: 'fixed', left: 0, top: 0, bottom: BOTTOMH, width: RAIL_W,
      bgcolor: theme.surface, borderRight: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      zIndex: 200, py: 1,
    }}>
      {/* Hamburger / logo */}
      <Tooltip title="Menu" placement="right">
        <IconButton
          onClick={onOpenDrawer}
          sx={{
            mb: 1, width: 40, height: 40, borderRadius: 2,
            '&:hover': { bgcolor: theme.surfaceHover },
          }}
        >
          <Box sx={{
            width: 26, height: 26, borderRadius: '7px',
            background: `linear-gradient(135deg, ${theme.gold} 0%, ${alpha(theme.gold, 0.6)} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Building2 size={13} color={isDark ? '#0B0E14' : '#fff'} strokeWidth={2.5} />
          </Box>
        </IconButton>
      </Tooltip>

      <Divider sx={{ borderColor: theme.border, width: 32, mb: 1 }} />

      {/* Nav items */}
      <Stack spacing={0.5} sx={{ flex: 1, width: '100%', px: 0.75 }}>
        {DRAWER_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = currentPath === item.path || currentPath.startsWith(`${item.path}/`);
          return (
            <Tooltip key={item.path} title={item.label} placement="right">
              <Box
                component={RouterLink}
                to={item.path}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  width: '100%', height: 40, borderRadius: 2,
                  textDecoration: 'none', transition: 'all 0.15s',
                  bgcolor: active ? alpha(theme.gold, 0.12) : 'transparent',
                  position: 'relative',
                  '&:hover': { bgcolor: active ? alpha(theme.gold, 0.16) : theme.surfaceHover },
                  ...(active && {
                    '&::before': {
                      content: '""', position: 'absolute', left: -6,
                      top: '50%', transform: 'translateY(-50%)',
                      width: 3, height: 18, borderRadius: '0 2px 2px 0',
                      bgcolor: theme.gold,
                    },
                  }),
                }}
              >
                <Icon size={18} color={active ? theme.gold : theme.muted} strokeWidth={active ? 2.5 : 2} />
              </Box>
            </Tooltip>
          );
        })}
      </Stack>

      <Divider sx={{ borderColor: theme.border, width: 32, mb: 1 }} />

      {/* Theme toggle */}
      <Tooltip title={isDark ? 'Light mode' : 'Dark mode'} placement="right">
        <IconButton
          onClick={toggleTheme}
          sx={{ width: 40, height: 40, borderRadius: 2, color: theme.muted, '&:hover': { bgcolor: theme.surfaceHover } }}
        >
          {isDark ? <Sun size={16} /> : <Moon size={16} />}
        </IconButton>
      </Tooltip>

      {/* User avatar */}
      <Tooltip title="Account" placement="right">
        <Avatar
          onClick={(e) => setMenuAnchor(e.currentTarget)}
          sx={{ width: 32, height: 32, mt: 0.5, bgcolor: alpha(theme.gold, 0.15), color: theme.gold, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
        >
          {user?.full_name?.[0] || user?.first_name?.[0] || 'U'}
        </Avatar>
      </Tooltip>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 2,
            boxShadow: `0 20px 60px ${alpha('#000', isDark ? 0.5 : 0.15)}`, minWidth: 180,
            '& .MuiMenuItem-root': { color: theme.muted, fontSize: 13, py: 1.25, px: 2, borderRadius: 1 },
            '& .MuiMenuItem-root:hover': { bgcolor: theme.surfaceHover, color: theme.text },
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ color: theme.text, fontSize: 13, fontWeight: 600 }} noWrap>
            {user?.full_name || `${user?.first_name} ${user?.last_name}`}
          </Typography>
          <Typography sx={{ color: theme.muted, fontSize: 11 }}>{user?.orgRole || 'Staff'}</Typography>
        </Box>
        <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
        <MenuItem onClick={() => { navigate(PATH_ADMIN.dashboard); setMenuAnchor(null); }}>
          <LayoutDashboard size={14} style={{ marginRight: 10 }} />
          Go to DeskOS
        </MenuItem>
        <Divider sx={{ borderColor: theme.border, my: 0.5 }} />
        <MenuItem
          onClick={() => { localStorage.removeItem('atlas_session'); navigate('/auth/login'); setMenuAnchor(null); }}
          sx={{ color: `${theme.danger} !important` }}
        >
          <LogOut size={14} style={{ marginRight: 10 }} />
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
}

// ── Top bar (portrait) ─────────────────────────────────────────────────────────
function TopBar({ onOpenDrawer, user, isDark, toggleTheme }) {
  const { theme } = useAtlasTheme();
  const [menuAnchor, setMenuAnchor] = useState(null);
  const navigate = useNavigate();

  return (
    <Box sx={{
      height: 52, display: 'flex', alignItems: 'center', px: 1.5, gap: 1,
      bgcolor: theme.surface, borderBottom: `1px solid ${theme.border}`,
      flexShrink: 0, zIndex: 10, position: 'sticky', top: 0,
    }}>
      <IconButton
        onClick={onOpenDrawer}
        sx={{ color: theme.muted, '&:hover': { color: theme.text, bgcolor: theme.surfaceHover }, borderRadius: 2 }}
      >
        <MenuIcon size={20} />
      </IconButton>

      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1 }}>
        <Box sx={{
          width: 26, height: 26, borderRadius: '7px',
          background: `linear-gradient(135deg, ${theme.gold} 0%, ${alpha(theme.gold, 0.6)} 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Building2 size={12} color={isDark ? '#0B0E14' : '#fff'} strokeWidth={2.5} />
        </Box>
        <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: 2, color: theme.text }}>ATLAS</Typography>
      </Stack>

      <IconButton onClick={toggleTheme} size="small" sx={{ color: theme.muted, '&:hover': { bgcolor: theme.surfaceHover }, borderRadius: 2 }}>
        {isDark ? <Sun size={17} /> : <Moon size={17} />}
      </IconButton>

      <Avatar
        onClick={(e) => setMenuAnchor(e.currentTarget)}
        sx={{ width: 30, height: 30, bgcolor: alpha(theme.gold, 0.15), color: theme.gold, fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
      >
        {user?.full_name?.[0] || user?.first_name?.[0] || 'U'}
      </Avatar>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: theme.surface, border: `1px solid ${theme.border}`, borderRadius: 2,
            boxShadow: `0 20px 60px ${alpha('#000', isDark ? 0.5 : 0.15)}`, minWidth: 200,
            '& .MuiMenuItem-root': { color: theme.muted, fontSize: 13, py: 1.25, px: 2 },
            '& .MuiMenuItem-root:hover': { bgcolor: theme.surfaceHover, color: theme.text },
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.25 }}>
          <Typography sx={{ color: theme.text, fontSize: 13, fontWeight: 600 }}>
            {user?.full_name || `${user?.first_name} ${user?.last_name}`}
          </Typography>
          <Typography sx={{ color: theme.muted, fontSize: 11 }}>{user?.orgRole || 'Staff'}</Typography>
        </Box>
        <Divider sx={{ borderColor: theme.border }} />
        <MenuItem onClick={() => { navigate(PATH_ADMIN.dashboard); setMenuAnchor(null); }}>
          <LayoutDashboard size={14} style={{ marginRight: 10 }} />
          Go to DeskOS
        </MenuItem>
        <Divider sx={{ borderColor: theme.border }} />
        <MenuItem
          onClick={() => { localStorage.removeItem('atlas_session'); navigate('/auth/login'); setMenuAnchor(null); }}
          sx={{ color: `${theme.danger} !important` }}
        >
          <LogOut size={14} style={{ marginRight: 10 }} />
          Sign out
        </MenuItem>
      </Menu>
    </Box>
  );
}

// ── Bottom Nav (always visible) ────────────────────────────────────────────────
function BottomNav({ currentPath, leftOffset }) {
  const { theme } = useAtlasTheme();
  return (
    <Box sx={{
      position: 'fixed', bottom: 0, left: leftOffset, right: 0, zIndex: 300,
      bgcolor: theme.surface, borderTop: `1px solid ${theme.border}`,
      height: BOTTOMH, display: 'flex',
      backdropFilter: 'blur(20px)',
      paddingBottom: 'env(safe-area-inset-bottom)',
    }}>
      {BOTTOM_NAV_ITEMS.map((item) => {
        const Icon = item.icon;
        const active = currentPath === item.path || currentPath.startsWith(`${item.path}/`);
        return (
          <Box
            key={item.path}
            component={RouterLink}
            to={item.path}
            sx={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 0.4, textDecoration: 'none',
              transition: 'all 0.15s', py: 1,
              borderTop: active ? `2px solid ${theme.gold}` : '2px solid transparent',
            }}
          >
            <Icon size={19} color={active ? theme.gold : theme.muted} strokeWidth={active ? 2.5 : 2} />
            <Typography sx={{ fontSize: 9.5, color: active ? theme.gold : theme.muted, fontWeight: active ? 700 : 400, letterSpacing: 0.3 }}>
              {item.label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}

// ── Nav Overlay Drawer ─────────────────────────────────────────────────────────
function NavOverlay({ open, onClose, currentPath, user, isDark, toggleTheme, campuses, selectedCampus, onCampusChange }) {
  const { theme } = useAtlasTheme();
  const navigate = useNavigate();

  const goToDeskOS = () => {
    onClose();
    navigate(PATH_ADMIN.dashboard);
  };

  const signOut = () => {
    localStorage.removeItem('atlas_session');
    navigate('/auth/login');
  };

  return (
    <Drawer
      anchor="left"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 260, bgcolor: theme.surface,
          border: 'none', borderRight: `1px solid ${theme.border}`,
          display: 'flex', flexDirection: 'column',
        },
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between"
        sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${theme.border}` }}
      >
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{
            width: 30, height: 30, borderRadius: '8px',
            background: `linear-gradient(135deg, ${theme.gold} 0%, ${alpha(theme.gold, 0.6)} 100%)`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 0 16px ${alpha(theme.gold, 0.25)}`,
          }}>
            <Building2 size={15} color={isDark ? '#0B0E14' : '#fff'} strokeWidth={2.5} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 13, letterSpacing: 2, color: theme.text, lineHeight: 1 }}>ATLAS</Typography>
            <Typography sx={{ fontWeight: 400, fontSize: 9, letterSpacing: 1.5, color: theme.muted }}>BY DESKOS</Typography>
          </Box>
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ color: theme.muted, '&:hover': { color: theme.text } }}>
          <X size={16} />
        </IconButton>
      </Stack>

      {/* Campus selector */}
      {campuses.length > 1 && (
        <Box sx={{ px: 2, pt: 2 }}>
          <Typography sx={{ fontSize: 10, color: theme.muted, letterSpacing: 1, fontWeight: 600, mb: 0.75, textTransform: 'uppercase' }}>Location</Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75}>
            <Chip
              label="All"
              size="small"
              onClick={() => onCampusChange('')}
              sx={{
                bgcolor: !selectedCampus ? theme.gold : theme.surfaceHover,
                color: !selectedCampus ? '#0B0E14' : theme.muted,
                fontWeight: 600, fontSize: 11, cursor: 'pointer',
                border: `1px solid ${!selectedCampus ? theme.gold : theme.border}`,
              }}
            />
            {campuses.map((c) => (
              <Chip
                key={c.id}
                label={c.name}
                size="small"
                onClick={() => onCampusChange(c.id)}
                sx={{
                  bgcolor: selectedCampus === c.id ? theme.gold : theme.surfaceHover,
                  color: selectedCampus === c.id ? '#0B0E14' : theme.muted,
                  fontWeight: 600, fontSize: 11, cursor: 'pointer',
                  border: `1px solid ${selectedCampus === c.id ? theme.gold : theme.border}`,
                }}
              />
            ))}
          </Stack>
        </Box>
      )}

      {/* Nav items */}
      <Stack spacing={0.5} sx={{ px: 1.5, pt: 2, flex: 1 }}>
        {DRAWER_NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = currentPath === item.path || currentPath.startsWith(`${item.path}/`);
          return (
            <Box
              key={item.path}
              component={RouterLink}
              to={item.path}
              onClick={onClose}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 2, py: 1.25, borderRadius: 2, textDecoration: 'none',
                bgcolor: active ? alpha(theme.gold, 0.1) : 'transparent',
                transition: 'all 0.15s',
                position: 'relative',
                '&:hover': { bgcolor: active ? alpha(theme.gold, 0.14) : theme.surfaceHover },
                ...(active && {
                  '&::before': {
                    content: '""', position: 'absolute', left: 0,
                    top: '50%', transform: 'translateY(-50%)',
                    width: 3, height: 18, borderRadius: '0 2px 2px 0',
                    bgcolor: theme.gold,
                  },
                }),
              }}
            >
              <Icon size={17} color={active ? theme.gold : theme.muted} strokeWidth={active ? 2.5 : 2} />
              <Typography sx={{ fontSize: 14, fontWeight: active ? 600 : 400, color: active ? theme.text : theme.muted }}>
                {item.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      <Divider sx={{ borderColor: theme.border, mx: 1.5 }} />

      {/* Footer */}
      <Stack spacing={0.5} sx={{ px: 1.5, py: 1.5 }}>
        {/* Theme toggle */}
        <Box
          onClick={toggleTheme}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2, py: 1.25, borderRadius: 2, cursor: 'pointer',
            '&:hover': { bgcolor: theme.surfaceHover },
          }}
        >
          {isDark ? <Sun size={16} color={theme.muted} /> : <Moon size={16} color={theme.muted} />}
          <Typography sx={{ fontSize: 13, color: theme.muted }}>{isDark ? 'Light mode' : 'Dark mode'}</Typography>
        </Box>

        {/* Go to DeskOS */}
        <Box
          onClick={goToDeskOS}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            px: 2, py: 1.25, borderRadius: 2, cursor: 'pointer',
            '&:hover': { bgcolor: theme.surfaceHover },
          }}
        >
          <LayoutDashboard size={16} color={theme.muted} />
          <Typography sx={{ fontSize: 13, color: theme.muted }}>Go to DeskOS</Typography>
          <ChevronRight size={13} color={theme.muted} style={{ marginLeft: 'auto' }} />
        </Box>

        {/* User row */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.25, borderRadius: 2 }}>
          <Avatar sx={{ width: 30, height: 30, bgcolor: alpha(theme.gold, 0.15), color: theme.gold, fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
            {user?.full_name?.[0] || user?.first_name?.[0] || 'U'}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: 12.5, color: theme.text, fontWeight: 500 }} noWrap>
              {user?.full_name || `${user?.first_name} ${user?.last_name}`}
            </Typography>
            <Typography sx={{ fontSize: 11, color: theme.muted }}>{user?.orgRole || 'Staff'}</Typography>
          </Box>
          <IconButton size="small" onClick={signOut} sx={{ color: theme.danger, flexShrink: 0 }}>
            <LogOut size={14} />
          </IconButton>
        </Box>
      </Stack>
    </Drawer>
  );
}

// ── Layout root ────────────────────────────────────────────────────────────────
export default function AtlasLayout() {
  const { user } = useAuth();
  const location = useLocation();
  const isPortrait = useMediaQuery('(orientation: portrait)');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [campuses, setCampuses] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState('');
  const [isDark, setIsDark] = useState(false); // light by default

  const theme = isDark ? DARK : LIGHT;
  const toggleTheme = () => setIsDark((d) => !d);
  const currentPath = location.pathname;

  // In landscape, content is pushed right by the icon rail
  const contentML = isPortrait ? 0 : RAIL_W;

  return (
    <AtlasThemeCtx.Provider value={{ theme, isDark, toggleTheme }}>
      <Box sx={{
        height: '100vh', width: '100vw',
        bgcolor: theme.bg, overflow: 'hidden',
        fontFamily: '"Inter", "SF Pro Display", system-ui, sans-serif',
      }}>
        {/* Icon rail — landscape only */}
        {!isPortrait && (
          <IconRail
            currentPath={currentPath}
            onOpenDrawer={() => setDrawerOpen(true)}
            user={user}
            isDark={isDark}
            toggleTheme={toggleTheme}
          />
        )}

        {/* Scrollable content area */}
        <Box sx={{
          ml: `${contentML}px`,
          height: `calc(100vh - ${BOTTOMH}px)`,
          overflowY: 'auto', overflowX: 'hidden',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
          '&::-webkit-scrollbar-thumb': { bgcolor: alpha(theme.muted, 0.2), borderRadius: 3 },
        }}>
          {/* Top bar — portrait only */}
          {isPortrait && (
            <TopBar
              onOpenDrawer={() => setDrawerOpen(true)}
              user={user}
              isDark={isDark}
              toggleTheme={toggleTheme}
            />
          )}

          <Outlet context={{ campuses, setCampuses, selectedCampus, setSelectedCampus }} />
        </Box>

        {/* Bottom nav — always */}
        <BottomNav currentPath={currentPath} leftOffset={contentML} />

        {/* Nav overlay drawer */}
        <NavOverlay
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          currentPath={currentPath}
          user={user}
          isDark={isDark}
          toggleTheme={toggleTheme}
          campuses={campuses}
          selectedCampus={selectedCampus}
          onCampusChange={setSelectedCampus}
        />
      </Box>
    </AtlasThemeCtx.Provider>
  );
}
